import { memo, ReactNode } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import CommentIcon from "@mui/icons-material/Comment";
import type { SxProps, Theme } from "@mui/material/styles";
import type { Payload as LegendPayload } from "recharts/types/component/DefaultLegendContent";

import { mergeSx } from "./styles";

// recharts' legend payload, with the series props it actually forwards.
type RechartsLegendEntry = LegendPayload & {
  payload?: { dataKey?: unknown; fill?: string; strokeDasharray?: unknown };
};

export type SwatchIcon =
  | "square"
  | "dot"
  | "ring"
  | "line"
  | "triangle"
  | "triangleLeft"
  | "comment";

// Colour swatch shared by chart legends and tooltips: 8px rounded square by
// default, circle for point markers, short dash-aware stroke for line series,
// triangle for point markers drawn as triangles (up: restriction markers;
// left-pointing: the segmentation N-1 marker beside its bar), speech bubble
// for the booking-curve comment markers.
export const Swatch = ({
  color,
  icon = "square",
  dashArray,
  lineCap,
  fillOpacity,
}: {
  color: string;
  icon?: SwatchIcon;
  dashArray?: string;
  // Cap of the line swatch's dashes (round for a dotted series).
  lineCap?: "butt" | "round" | "square";
  // Mirrors a translucent fill (e.g. a 40 % bar) on square/dot swatches.
  fillOpacity?: number;
}) =>
  icon === "triangle" ? (
    <svg width={10} height={9} style={{ flexShrink: 0 }}>
      <path d="M 5 0.5 L 9.5 8.5 L 0.5 8.5 Z" fill={color} />
    </svg>
  ) : icon === "comment" ? (
    <CommentIcon htmlColor={color} sx={{ fontSize: 14, flexShrink: 0 }} />
  ) : icon === "triangleLeft" ? (
    <svg width={9} height={10} style={{ flexShrink: 0 }}>
      <path d="M 0.5 5 L 8.5 0.5 L 8.5 9.5 Z" fill={color} />
    </svg>
  ) : icon === "ring" ? (
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        border: "2px solid",
        borderColor: color,
        bgcolor: "common.white",
        flexShrink: 0,
      }}
    />
  ) : icon === "line" ? (
    <svg width={16} height={4} style={{ flexShrink: 0 }}>
      <line
        x1={0}
        y1={2}
        x2={16}
        y2={2}
        stroke={color}
        strokeWidth={2}
        strokeDasharray={dashArray}
        strokeLinecap={lineCap}
      />
    </svg>
  ) : (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: icon === "dot" ? "50%" : "2px",
        bgcolor: color,
        opacity: fillOpacity,
        flexShrink: 0,
      }}
    />
  );

export interface ChartLegendEntry {
  key: string;
  // recharts series dataKey — for charts whose show/hide toggle is keyed by
  // dataKey rather than by series name (remap `key` from it in that case).
  dataKey?: string;
  label: ReactNode;
  color: string;
  icon?: SwatchIcon;
  dashArray?: string;
  lineCap?: "butt" | "round" | "square";
  fillOpacity?: number;
  // Toggled off by the user: dimmed and struck through.
  inactive?: boolean;
  // Nothing to draw for it (no data): dimmed only, it was not switched off.
  muted?: boolean;
}

// Maps a recharts `<Legend content>` payload to ChartLegend entries so any
// recharts chart can opt into the shared legend look. `entry.type` is the
// series' `legendType`; entries with `legendType="none"` are dropped, like
// recharts' own legend does. Areas with `stroke="none"` carry their colour
// in `payload.fill`, hence the fallback.
export const rechartsLegendEntries = (
  payload?: readonly RechartsLegendEntry[]
): ChartLegendEntry[] =>
  (payload ?? [])
    .filter((entry) => entry.type !== "none")
    .map((entry) => ({
      key: String(entry.value),
      dataKey:
        entry.dataKey != null
          ? String(entry.dataKey)
          : entry.payload?.dataKey != null
          ? String(entry.payload.dataKey)
          : undefined,
      label: entry.value,
      color:
        (entry.color === "none" ? undefined : entry.color) ??
        entry.payload?.fill ??
        "",
      icon:
        entry.type === "square" || entry.type === "rect"
          ? "square"
          : entry.type === "circle"
          ? "dot"
          : "line",
      dashArray: entry.payload?.strokeDasharray
        ? String(entry.payload.strokeDasharray)
        : undefined,
      inactive: !!entry.inactive,
    }));

// Recharts renders a *function* `content` through `React.createElement(content,
// props)`, so an inline arrow is a brand-new component type on every render and
// React remounts the whole legend subtree instead of updating it. The buttons
// then get destroyed between mousedown and mouseup, and the browser never fires
// `click` on the (now detached) node the press started on: the toggle looks
// dead. Passing this component as an *element* keeps recharts on its
// cloneElement path, so the type stays stable and the DOM is updated in place.
export const RechartsChartLegend = ({
  payload,
  mapEntry,
  onEntryClick,
  sx,
}: {
  // Injected by recharts when it clones this element.
  payload?: readonly RechartsLegendEntry[];
  mapEntry?: (entry: ChartLegendEntry) => ChartLegendEntry;
  onEntryClick?: (key: string) => void;
  sx?: SxProps<Theme>;
}) => {
  const entries = rechartsLegendEntries(payload);
  return (
    <ChartLegend
      entries={mapEntry ? entries.map(mapEntry) : entries}
      onEntryClick={onEntryClick}
      sx={sx}
    />
  );
};

// Shared chart legend (the Cockpit "Avance/Retard" look): centered wrapping
// row of swatch + caption items. With `onEntryClick`, items render as real
// buttons (keyboard-focusable, `aria-pressed` = series shown) toggling
// show/hide; toggled-off entries render dimmed via `inactive`.
const ChartLegend = ({
  entries,
  onEntryClick,
  sx,
}: {
  entries: ChartLegendEntry[];
  onEntryClick?: (key: string) => void;
  sx?: SxProps<Theme>;
}) => (
  <Box
    sx={[
      {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "4px 16px",
      },
      ...mergeSx(sx),
    ]}
  >
    {entries.map((entry) => {
      const itemSx = {
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        opacity: entry.inactive || entry.muted ? 0.4 : 1,
      };
      const content = (
        <>
          <Swatch
            color={entry.color}
            icon={entry.icon}
            dashArray={entry.dashArray}
            lineCap={entry.lineCap}
            fillOpacity={entry.fillOpacity}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={entry.inactive ? { textDecoration: "line-through" } : undefined}
          >
            {entry.label}
          </Typography>
        </>
      );
      return onEntryClick ? (
        <ButtonBase
          key={entry.key}
          onClick={() => onEntryClick(entry.key)}
          aria-pressed={!entry.inactive}
          // ButtonBase is `position: relative` for its ripple; a positioned
          // legend rendered after the chart paints over the recharts tooltip
          // (absolute, no z-index). Keep it unpositioned and drop the ripple.
          disableRipple
          sx={{
            ...itemSx,
            position: "static",
            borderRadius: 0.5,
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
            },
          }}
        >
          {content}
        </ButtonBase>
      ) : (
        <Box key={entry.key} sx={itemSx}>
          {content}
        </Box>
      );
    })}
  </Box>
);

export default memo(ChartLegend);
